import type { ReactNode } from 'react';
import { Modal } from '@/components/Modal';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { buttonStyles } from '@/styles/buttons';

interface ConfirmDialogProps {
  isOpen: boolean;
  mode: 'confirm' | 'alert';
  title?: string;
  message: ReactNode;
  confirmText: string;
  cancelText: string;
  variant: 'default' | 'danger';
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * ConfirmDialog Component
 *
 * A styled confirmation/alert dialog that replaces native browser dialogs.
 * Built on top of the Modal component for consistent styling and accessibility.
 *
 * Features:
 * - Supports both confirm (OK/Cancel) and alert (OK only) modes
 * - Danger variant for destructive actions (red confirm button)
 * - ReactNode message support for rich content
 * - Mobile-responsive design
 * - Focus trap and keyboard navigation (via Modal)
 * - WCAG 2.1 accessible
 *
 * @internal This component is managed by DialogContext - use useDialog() hook instead
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  mode,
  title,
  message,
  confirmText,
  cancelText,
  variant,
  onConfirm,
  onCancel,
}) => {
  const { isMobile } = useMediaQuery();

  // Content styles
  const contentStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  };

  const messageStyle: React.CSSProperties = {
    fontSize: isMobile ? '0.9375rem' : '1rem',
    lineHeight: 1.6,
    color: 'var(--text-color)',
  };

  const buttonContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: isMobile ? 'column-reverse' : 'row',
    justifyContent: 'flex-end',
    gap: '0.75rem',
  };

  // Button styles based on variant
  const confirmButtonStyle: React.CSSProperties =
    variant === 'danger'
      ? {
          ...buttonStyles.danger,
          flex: isMobile ? 1 : undefined,
          minWidth: isMobile ? undefined : '100px',
        }
      : {
          ...buttonStyles.primary,
          flex: isMobile ? 1 : undefined,
          minWidth: isMobile ? undefined : '100px',
        };

  const cancelButtonStyle: React.CSSProperties = {
    ...buttonStyles.cancel,
    flex: isMobile ? 1 : undefined,
    minWidth: isMobile ? undefined : '100px',
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={title}
      maxWidth="450px"
      zIndex={10001} // Above other modals
      showCloseButton={false} // Use buttons instead
      ariaLabel={title ? undefined : 'Confirmation dialog'}
    >
      <div style={contentStyle}>
        <div style={messageStyle}>{message}</div>

        <div style={buttonContainerStyle}>
          {/* Cancel button (only in confirm mode) */}
          {mode === 'confirm' && (
            <button type="button" style={cancelButtonStyle} onClick={onCancel}>
              {cancelText}
            </button>
          )}

          {/* Confirm/OK button */}
          <button type="button" style={confirmButtonStyle} onClick={onConfirm} autoFocus>
            {confirmText}
          </button>
        </div>
      </div>
    </Modal>
  );
};
